import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowUp,
  FiArrowDown,
  FiBookOpen,
  FiEdit2,
  FiExternalLink,
  FiPlus,
  FiTrash2,
  FiX,
  FiDownloadCloud,
} from 'react-icons/fi';
import {
  LAYOUT_TYPES,
  createLibrary,
  deleteLibrary,
  listLibraries,
  reorderLibraries,
  updateLibrary,
} from './curriculumLibrariesService';
import { getPendingLegacySources, migrateLegacyCurriculum } from './curriculumMigration';
import './Curriculum.css';
import './CurriculumHub.css';

const MASTER_DELETE_PIN = '1098';

const LAYOUT_OPTIONS = [
  {
    value: LAYOUT_TYPES.CATEGORY,
    title: 'Category Carousel',
    description: 'Group textbooks into categories shown as horizontal rows. Best for broad libraries like AP.',
  },
  {
    value: LAYOUT_TYPES.SIMPLE,
    title: 'Simple Library',
    description: 'All textbooks in one grid, no categories. Best for focused libraries like SAT or ACT.',
  },
];

function LayoutPreview({ type }) {
  if (type === LAYOUT_TYPES.CATEGORY) {
    return (
      <div className="chub__preview chub__preview--category" aria-hidden="true">
        <span className="chub__previewLabel" />
        <div className="chub__previewRow"><span /><span /><span /><span /></div>
        <span className="chub__previewLabel" />
        <div className="chub__previewRow"><span /><span /><span /><span /></div>
      </div>
    );
  }
  return (
    <div className="chub__preview chub__preview--simple" aria-hidden="true">
      <div className="chub__previewGrid">
        <span /><span /><span /><span /><span /><span /><span /><span />
      </div>
    </div>
  );
}

// Shared create / edit dialog. Layout is chosen only at creation time; editing
// changes name, description, and the optional logo.
function LibraryFormModal({ mode, library, onClose, onSubmit, submitting }) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState(library?.name || '');
  const [description, setDescription] = useState(library?.description || '');
  const [layoutType, setLayoutType] = useState(library?.layoutType || LAYOUT_TYPES.SIMPLE);
  const [logoMode, setLogoMode] = useState(isEdit ? 'keep' : 'none');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');

  const submit = () => {
    onSubmit({ name, description, layoutType, logoMode, logoFile, logoUrl });
  };

  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className="apcModal__card chub__formCard">
        <div className="apcModal__header">
          <h3>{isEdit ? 'Edit Library' : 'Create Curriculum Library'}</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          <label className="apcField">
            <span>Library Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spectrum Curriculum" autoFocus />
          </label>

          <label className="apcField">
            <span>Description (optional)</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short summary (not shown on cards)" />
          </label>

          <label className="apcField">
            <span>Logo (optional)</span>
            <select value={logoMode} onChange={(e) => setLogoMode(e.target.value)}>
              {isEdit ? (
                <>
                  <option value="keep">Keep Current Logo</option>
                  <option value="file">Upload New Logo</option>
                  <option value="url">Use Logo URL</option>
                  <option value="remove">Remove Logo</option>
                </>
              ) : (
                <>
                  <option value="none">Default Icon</option>
                  <option value="file">Upload Logo</option>
                  <option value="url">Paste Logo URL</option>
                </>
              )}
            </select>
          </label>

          {isEdit && logoMode === 'keep' && (
            <div className="chub__logoPreviewRow">
              {library?.logoUrl ? (
                <img src={library.logoUrl} alt="" className="chub__logoPreview" />
              ) : (
                <span className="chub__logoPreviewNone">No logo set — default icon shown.</span>
              )}
            </div>
          )}

          {logoMode === 'url' && (
            <label className="apcField">
              <span>Logo URL</span>
              <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." />
            </label>
          )}

          {logoMode === 'file' && (
            <label className="apcField">
              <span>Logo Image</span>
              <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
            </label>
          )}

          {!isEdit && (
            <div className="apcField">
              <span>Display Layout</span>
              <div className="chub__layoutChoices">
                {LAYOUT_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`chub__layoutChoice ${layoutType === option.value ? 'is-selected' : ''}`}
                    onClick={() => setLayoutType(option.value)}
                  >
                    <LayoutPreview type={option.value} />
                    <div className="chub__layoutChoiceText">
                      <strong>{option.title}</strong>
                      <span>{option.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create & Open'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Step 1 of the destructive-delete safeguard: master administrator PIN.
function DeletePinModal({ library, onClose, onVerified }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const verify = () => {
    if (pin === MASTER_DELETE_PIN) {
      onVerified();
    } else {
      setError('Incorrect PIN. Deletion cancelled steps remain locked.');
      setPin('');
    }
  };

  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className="apcModal__card apcModal__card--mini">
        <div className="apcModal__header">
          <h3>Administrator PIN Required</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          <p className="chub__deleteText">
            Deleting <strong>{library.name}</strong> is permanent. Enter the master administrator PIN to continue.
          </p>
          <label className="apcField">
            <span>Master PIN</span>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') verify();
              }}
              placeholder="••••"
              autoFocus
            />
          </label>
          {error && <div className="chub__deleteError">{error}</div>}
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary" onClick={verify} disabled={!pin}>
            Verify PIN
          </button>
        </div>
      </div>
    </div>
  );
}

// Step 2: final explicit confirmation after the PIN is accepted.
function DeleteConfirmModal({ library, submitting, onClose, onConfirm }) {
  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className="apcModal__card apcModal__card--mini">
        <div className="apcModal__header">
          <h3>Confirm Permanent Deletion</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          <p className="chub__deleteText">
            This will permanently delete the entire <strong>{library.name}</strong> library, including all of its
            categories, textbooks, and resources. This action cannot be undone.
          </p>
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary apcBtn--danger" onClick={onConfirm} disabled={submitting}>
            {submitting ? 'Deleting...' : 'Delete Library'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lightweight right-click context menu rendered at the cursor. Reordering and
// deletion are admin-only (gated by `canManage`); Open/Edit are always present.
function CardContextMenu({ menu, canManage, total, onOpen, onEdit, onMoveUp, onMoveDown, onDelete, onClose }) {
  const ref = useRef(null);
  const canMoveUp = menu.index > 0;
  const canMoveDown = menu.index < total - 1;

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('click', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Keep the menu inside the viewport.
  const style = {
    top: Math.min(menu.y, window.innerHeight - 160),
    left: Math.min(menu.x, window.innerWidth - 200),
  };

  return (
    <div
      ref={ref}
      className="chub__menu"
      style={style}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button type="button" className="chub__menuItem" onClick={onOpen}>
        <FiExternalLink /> Open Library
      </button>
      <button type="button" className="chub__menuItem" onClick={onEdit}>
        <FiEdit2 /> Edit Library
      </button>
      {canManage && (
        <>
          <div className="chub__menuDivider" />
          <button type="button" className="chub__menuItem" onClick={onMoveUp} disabled={!canMoveUp}>
            <FiArrowUp /> Move Up
          </button>
          <button type="button" className="chub__menuItem" onClick={onMoveDown} disabled={!canMoveDown}>
            <FiArrowDown /> Move Down
          </button>
          <div className="chub__menuDivider" />
          <button type="button" className="chub__menuItem chub__menuItem--danger" onClick={onDelete}>
            <FiTrash2 /> Delete Library
          </button>
        </>
      )}
    </div>
  );
}

// The Curriculum Hub: a clean library launcher. Each card shows only a logo (or
// the default icon) and the library title. Management actions live in a
// right-click context menu; deleting a library requires a PIN + confirmation.
export default function CurriculumHub({ canDelete = true, onOpenLibrary }) {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menu, setMenu] = useState(null);
  const [deleteFlow, setDeleteFlow] = useState(null); // { stage: 'pin' | 'confirm', library }
  const [pendingLegacy, setPendingLegacy] = useState([]);
  const [importing, setImporting] = useState(false);

  const loadHub = useCallback(async () => {
    setLoading(true);
    try {
      const libs = await listLibraries();
      setLibraries(libs);
      if (canDelete) {
        try {
          setPendingLegacy(await getPendingLegacySources());
        } catch (error) {
          setPendingLegacy([]);
        }
      }
    } catch (error) {
      alert(error.message || 'Failed to load curriculum libraries.');
    } finally {
      setLoading(false);
    }
  }, [canDelete]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  const handleCreate = async ({ name, description, layoutType, logoMode, logoFile, logoUrl }) => {
    try {
      setSubmitting(true);
      const created = await createLibrary({ name, description, layoutType, logoMode, logoFile, logoUrl });
      setShowCreate(false);
      onOpenLibrary(created.slug);
    } catch (error) {
      alert(error.message || 'Failed to create library.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async ({ name, description, logoMode, logoFile, logoUrl }) => {
    if (!editing) return;
    try {
      setSubmitting(true);
      await updateLibrary({
        libraryId: editing.id,
        name,
        description,
        logoUpdate: { mode: logoMode, file: logoFile, url: logoUrl },
      });
      setEditing(null);
      await loadHub();
    } catch (error) {
      alert(error.message || 'Failed to update library.');
    } finally {
      setSubmitting(false);
    }
  };

  const performDelete = async () => {
    if (!deleteFlow?.library) return;
    try {
      setSubmitting(true);
      await deleteLibrary({ libraryId: deleteFlow.library.id });
      setDeleteFlow(null);
      await loadHub();
    } catch (error) {
      alert(error.message || 'Failed to delete library.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReorder = async (fromIndex, direction) => {
    try {
      setSubmitting(true);
      await reorderLibraries({ libraries, fromIndex, direction });
      await loadHub();
    } catch (error) {
      alert(error.message || 'Failed to reorder libraries.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportLegacy = async () => {
    if (!window.confirm('Import the existing AP, SAT, and ACT libraries into the new system? This is safe to run once.')) return;
    try {
      setImporting(true);
      await migrateLegacyCurriculum();
      await loadHub();
    } catch (error) {
      alert(error.message || 'Failed to import legacy curriculum.');
    } finally {
      setImporting(false);
    }
  };

  const openContextMenu = (event, library, index) => {
    event.preventDefault();
    setMenu({ x: event.clientX, y: event.clientY, library, index });
  };

  const isEmpty = useMemo(() => !loading && libraries.length === 0, [loading, libraries]);

  return (
    <div className="chub">
      <div className="chub__header">
        <div className="chub__titleWrap">
          <h1>Curriculum</h1>
          <p>Select a library to open, or create a new one.</p>
        </div>

        <div className="chub__headerActions">
          {canDelete && pendingLegacy.length > 0 && (
            <button type="button" className="apcBtn apcBtn--ghost" onClick={handleImportLegacy} disabled={importing}>
              <FiDownloadCloud /> {importing ? 'Importing...' : 'Import existing AP/SAT/ACT'}
            </button>
          )}
          <button type="button" className="apcBtn apcBtn--primary" onClick={() => setShowCreate(true)} disabled={submitting}>
            <FiPlus /> Create Library
          </button>
        </div>
      </div>

      {loading ? (
        <div className="apcLoading">Loading curriculum libraries...</div>
      ) : isEmpty ? (
        <div className="chub__empty">
          <FiBookOpen />
          <h3>No curriculum libraries yet</h3>
          <p>
            {canDelete
              ? 'Create your first library, or import the existing AP/SAT/ACT content using the buttons above.'
              : 'Create your first library using the button above.'}
          </p>
        </div>
      ) : (
        <div className="chub__grid">
          {libraries.map((library, index) => (
            <article key={library.id} className="chub__card">
              <button
                type="button"
                className="chub__cardBtn"
                onClick={() => onOpenLibrary(library.slug)}
                onContextMenu={(e) => openContextMenu(e, library, index)}
                title={library.name}
              >
                <div className="chub__logo">
                  {library.logoUrl ? (
                    <img src={library.logoUrl} alt="" className="chub__logoImg" />
                  ) : (
                    <FiBookOpen className="chub__logoIcon" />
                  )}
                </div>
                <div className="chub__cardTitle">{library.name}</div>
              </button>
            </article>
          ))}
        </div>
      )}

      {menu && (
        <CardContextMenu
          menu={menu}
          canManage={canDelete}
          total={libraries.length}
          onOpen={() => {
            onOpenLibrary(menu.library.slug);
            setMenu(null);
          }}
          onEdit={() => {
            setEditing(menu.library);
            setMenu(null);
          }}
          onMoveUp={() => {
            handleReorder(menu.index, 'up');
            setMenu(null);
          }}
          onMoveDown={() => {
            handleReorder(menu.index, 'down');
            setMenu(null);
          }}
          onDelete={() => {
            setDeleteFlow({ stage: 'pin', library: menu.library });
            setMenu(null);
          }}
          onClose={() => setMenu(null)}
        />
      )}

      {showCreate && (
        <LibraryFormModal mode="create" onClose={() => setShowCreate(false)} onSubmit={handleCreate} submitting={submitting} />
      )}

      {editing && (
        <LibraryFormModal
          mode="edit"
          library={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleEditSubmit}
          submitting={submitting}
        />
      )}

      {deleteFlow?.stage === 'pin' && (
        <DeletePinModal
          library={deleteFlow.library}
          onClose={() => setDeleteFlow(null)}
          onVerified={() => setDeleteFlow((prev) => ({ ...prev, stage: 'confirm' }))}
        />
      )}

      {deleteFlow?.stage === 'confirm' && (
        <DeleteConfirmModal
          library={deleteFlow.library}
          submitting={submitting}
          onClose={() => setDeleteFlow(null)}
          onConfirm={performDelete}
        />
      )}
    </div>
  );
}
